import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowDownRight, ArrowUpRight, ExternalLink } from "lucide-react";
import { fmtUsd, fmtPrice } from "@/lib/format";

export const fmtMcap = (v) => (v == null ? "—" : v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`);
export const fmtHold = (h) => (h == null ? "—" : h < 1 ? `${Math.max(1, Math.round(h * 60))}m` : h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`);
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function TradeFillsDialog({ trade, onClose }) {
  const t = trade;
  if (!t) return null;
  const fills = t.fills || [];
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass border-[#334566] max-w-xl max-h-[88vh] overflow-y-auto" data-testid="trade-fills-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">${t.ticker} <span className="text-slate-500 font-normal text-sm">{t.name}</span></DialogTitle>
          <DialogDescription className="text-slate-400">Every entry and exit read from the chain.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          {[["Entry mcap", fmtMcap(t.entryMcap)], ["Exit mcap", t.status === "closed" ? fmtMcap(t.exitMcap) : "open"], ["Hold time", t.status === "closed" ? fmtHold(t.holdHours) : "holding"], ["Size", fmtUsd(t.amount)]].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-[#222F43] bg-[#0d1420] p-2.5" data-testid={`fill-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{k}</div>
              <div className="font-mono font-bold text-sm mt-0.5">{v}</div>
            </div>
          ))}
        </div>
        {fills.length === 0 ? <p className="text-sm text-slate-500">No fill-level data for this trade (entered manually).</p> : (
          <div className="divide-y divide-[#222F43] rounded-lg border border-[#222F43]" data-testid="fills-list">
            {fills.map((f, i) => (
              <div key={f.sig || i} className="flex items-center gap-3 px-3 py-2 text-xs">
                {f.side === "buy" ? <ArrowDownRight className="h-4 w-4 text-emerald-400 shrink-0" /> : <ArrowUpRight className="h-4 w-4 text-red-400 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{f.side === "buy" ? "Buy" : "Sell"} <span className="text-slate-500 font-normal">· {when(f.at)}</span></div>
                  <div className="font-mono text-slate-500 truncate">{Number(f.tokens).toLocaleString(undefined, { maximumFractionDigits: 0 })} tokens @ {fmtPrice(f.price)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono">{fmtUsd(f.usd)}</div>
                  <div className="font-mono text-slate-500">{f.sol ? `${f.sol} SOL` : ""}</div>
                </div>
                {f.sig && <a href={`https://solscan.io/tx/${f.sig}`} target="_blank" rel="noreferrer" className="text-slate-600 hover:text-emerald-400" title="View on Solscan"><ExternalLink className="h-3.5 w-3.5" /></a>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
